import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaDownload } from 'react-icons/fa';
import { AgGridTable, Pagination, Button } from 'components/common';
import {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  exportBookings,
} from 'redux/booking/car/carBookingSlice';
import { columnDefs } from './Columns';

// Per-status grid. Quick filters live on the page and arrive via `extraParams`
// (shared across all tabs); this component just fetches + paginates + exports.
const CarBookingGrid = ({ stateKey, loadThunk, exportStatus, extraParams }) => {
  const dispatch = useDispatch();
  const entry = useSelector((s) => s.booking_car[stateKey]) || {};
  const loading = useSelector((s) => s.booking_car.loading);
  const exporting = useSelector((s) => s.booking_car.exporting);

  const { data = [], totalRows = 0, totalPagesCount = 1, page = 1, pageSize = 50, currentParams = {} } = entry;

  const perPageRef = useRef(pageSize);
  useEffect(() => { perPageRef.current = pageSize; }, [pageSize]);

  const extraKey = JSON.stringify(extraParams || {});

  const runFetch = useCallback((pageNum) => {
    const params = { page: pageNum, per_page: perPageRef.current, ...(extraParams || {}) };
    dispatch(setNestedPage({ key: stateKey, page: pageNum }));
    dispatch(setNestedParams({ key: stateKey, params }));
    dispatch(loadThunk(params));
  }, [dispatch, stateKey, loadThunk, extraParams]);

  // Refetch page 1 on mount and whenever the shared quick filters change.
  useEffect(() => { runFetch(1); }, [extraKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (p) => runFetch(p);
  const handlePageSizeChange = (sz) => { perPageRef.current = sz; dispatch(setNestedPageSize({ key: stateKey, pageSize: sz })); runFetch(1); };
  const handleExport = () => dispatch(exportBookings({ ...currentParams, export_status: exportStatus }));

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <span className="tw-text-xs tw-text-slate-400 tw-font-medium">{(totalRows || 0).toLocaleString()} records</span>
        <Button variant="secondary" size="sm" leftIcon={FaDownload} loading={exporting} onClick={handleExport}>Export</Button>
      </div>

      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
        <AgGridTable rowData={data} columnDefs={columnDefs} pagination={false} filter={false} height="60vh" loading={loading} />
        <Pagination
          currentPage={page}
          totalPages={totalPagesCount}
          totalResults={totalRows}
          perPage={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[50, 100, 200]}
        />
      </div>
    </div>
  );
};

CarBookingGrid.propTypes = {
  stateKey: PropTypes.string.isRequired,
  loadThunk: PropTypes.func.isRequired,
  exportStatus: PropTypes.string,
  extraParams: PropTypes.object,
};

export default CarBookingGrid;
